import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import { useAppContext } from '../../context/AppContext';
import { useTbkConnection } from '../../context/TbkConnectionContext';
import { useXSignConnection } from '../../context/XSignConnectionContext';
import axios from 'axios';
import { getOrderEndpoint, getPaymentProvider } from '../../services/PaymentProviderService';
import { getStatus as getGetnetStatus, cancelSale as cancelGetnetSale } from '../../services/GetnetService';
import logo from '../../assets/logo-danes.png';
import BrandLogo from '../common/BrandLogo';

const OperateCardScreen = () => {
    const { total, items, clearCart } = useCart();
    const { sessionData, settings, orderType, setOrderType, paymentMethods } = useAppContext();
    const { isConnected: isTbkConnected } = useTbkConnection();
    const { isConnected: isXSignConnected } = useXSignConnection();
    const [isProcessing, setIsProcessing] = useState(false);
    const [isFetching, setIsFetching] = useState(false);
    const [isTBKError, setIsTBKError] = useState(false);
    const [tbkErrorMessage, setTbkErrorMessage] = useState("");
    const [error, setError] = useState('');
    const [chargedWithoutOrder, setChargedWithoutOrder] = useState(false);
    const [currentStep, setCurrentStep] = useState('waiting');
    const [animationStep, setAnimationStep] = useState(0);
    const [provider, setProvider] = useState('transbank');
    const [isCancelling, setIsCancelling] = useState(false);

    const isGetnet = provider === 'getnet';
    const terminalName = isGetnet ? 'Getnet' : 'TBK';

    const PAYMENT_SESSION_KEY = `payment_session_${sessionData?.session_id || 'default'}`;

    const [hasProcessStarted, setHasProcessStarted] = useState(() => {
        return sessionStorage.getItem(PAYMENT_SESSION_KEY + '_started') === 'true';
    });

    const [hasCompleted, setHasCompleted] = useState(() => {
        return sessionStorage.getItem(PAYMENT_SESSION_KEY + '_completed') === 'true';
    });

    const processRef = useRef(false);
    const navigationRef = useRef(false);

    const [optimisticConnections, setOptimisticConnections] = useState({
        tbk: true,
        xsign: true
    });

    const navigate = useNavigate();

    const markProcessStarted = useCallback(() => {
        setHasProcessStarted(true);
        sessionStorage.setItem(PAYMENT_SESSION_KEY + '_started', 'true');
        processRef.current = true;
    }, [PAYMENT_SESSION_KEY]);

    const markProcessCompleted = useCallback(() => {
        setHasCompleted(true);
        sessionStorage.setItem(PAYMENT_SESSION_KEY + '_completed', 'true');
    }, [PAYMENT_SESSION_KEY]);

    useEffect(() => {
        return () => {
            sessionStorage.removeItem(PAYMENT_SESSION_KEY + '_started');
            sessionStorage.removeItem(PAYMENT_SESSION_KEY + '_completed');
        };
    }, [PAYMENT_SESSION_KEY]);

    useEffect(() => {
        const interval = setInterval(() => {
            setAnimationStep(prev => (prev + 1) % 3);
        }, 1500);

        return () => clearInterval(interval);
    }, []);

    const apiUrl = settings?.apiUrl || 'http://localhost:9000';

    useEffect(() => {
        getPaymentProvider(apiUrl).then(setProvider);
    }, [apiUrl]);

    // Estado de XSign para el aviso de la pantalla de pago. Antes dependía del contexto, que
    // empieza en "no conectado" y solo se actualiza si el indicador de XSign está visible (en
    // el pago no lo está), así que el aviso "Verificando conexion XSign..." salía siempre aunque
    // XSign estuviera abierto. Ahora se consulta XSign directamente (mismo ping que usa el cobro).
    useEffect(() => {
        if (hasCompleted) return undefined;
        let cancelado = false;
        const comprobarXSign = async () => {
            try {
                await axios.get('http://127.0.0.1:5999/ping', { timeout: 3000 });
                if (!cancelado) setOptimisticConnections(prev => ({ ...prev, xsign: true }));
            } catch (e) {
                if (!cancelado) setOptimisticConnections(prev => ({ ...prev, xsign: false }));
            }
        };
        comprobarXSign();
        const timer = setInterval(comprobarXSign, 5000);
        return () => { cancelado = true; clearInterval(timer); };
    }, [hasCompleted]);


    const handlePayment = useCallback(async (forceSimulate = false) => {
        if (hasCompleted || navigationRef.current ||
            sessionStorage.getItem(PAYMENT_SESSION_KEY + '_completed') === 'true') {
            console.log('Proceso ya completado segun sessionStorage, evitando re-ejecucion');
            return;
        }

        if (!total || total <= 0) {
            console.log('Total en 0 (carrito vacío), se omite el pago');
            return;
        }

        console.log(forceSimulate
            ? 'Iniciando pago SIMULADO (aprobado)'
            : `Iniciando proceso de pago (${provider})`);
        markProcessStarted();
        setIsFetching(true);
        setIsTBKError(false);
        setTbkErrorMessage('');
        setCurrentStep('processing');
        setIsProcessing(true);
        setError('');

        const ticket = Math.floor(Math.random() * 90000) + 10000;
        const customerWhatsapp = sessionStorage.getItem('customer_whatsapp');

        const products = items.map((item) => {
            const itemId = String(item.id);
            const productId = itemId.includes('-') ? itemId.split('-')[0] : itemId;
            const quantity = parseInt(item.quantity);
            const rawLines = Array.isArray(item.attribute_lines) ? item.attribute_lines : [];

            // Precio base como residual: base = precio total del item - extras que el proxy
            // volvera a sumar (is_addition). Asi base + is_addition = totalPrice = lo que ya
            // cobro la maquina, sin importar lo anidado. Mismo modelo que el PDV.
            let costoExtras = 0;
            rawLines.forEach(l => {
                if (l.is_addition) costoExtras += (parseFloat(l.price) || 0) * (parseInt(l.qty) || 0);
                if (Array.isArray(l.sub_attribute_lines)) {
                    l.sub_attribute_lines.forEach(s => {
                        if (s.is_addition) costoExtras += (parseFloat(s.price) || 0) * (parseInt(s.qty) || 0);
                    });
                }
            });
            const basePrice = Math.round((parseFloat(item.price) || 0) - costoExtras);

            // Las cantidades de atributos se escalan por la cantidad del item (igual que el PDV),
            // para que el proxy sume los extras tantas veces como unidades haya.
            const scaledLines = rawLines.map(l => {
                const line = { ...l, qty: (parseInt(l.qty) || 0) * quantity };
                if (Array.isArray(l.sub_attribute_lines)) {
                    line.sub_attribute_lines = l.sub_attribute_lines.map(s => ({
                        ...s, qty: (parseInt(s.qty) || 0) * quantity
                    }));
                }
                return line;
            });

            const productData = {
                product_id: parseInt(productId),
                name: item.name,
                price: basePrice,
                qty: quantity,
                customization: item.customDescription || '',
                attribute_lines: scaledLines
            };

            // Lo que va a Odoo (selected_attributes / nested_attributes) también se escala por la cantidad,
            // igual que attribute_lines y que el PDV: Odoo lo compara con "por defecto x unidades" para
            // registrar los extras. Antes iba por unidad y con 3 hamburguesas quedaba 1 solo agrandado
            // en Odoo aunque la boleta cobraba 3.
            const escalar = (mapa) => Object.fromEntries(
                Object.entries(mapa || {}).map(([id, q]) => [id, (parseInt(q) || 0) * quantity])
            );

            if (item.selectedAttributes && Object.keys(item.selectedAttributes).length > 0) {
                productData.selected_attributes = JSON.stringify(escalar(item.selectedAttributes));
            }

            if (item.nested_attributes) {
                let anidados = item.nested_attributes;
                if (typeof anidados === 'string') {
                    try { anidados = JSON.parse(anidados); } catch (e) { anidados = {}; }
                }
                productData.nested_attributes = JSON.stringify(Object.fromEntries(
                    Object.entries(anidados || {}).map(([padre, subMapa]) => [padre, escalar(subMapa)])
                ));
            }

            return productData;
        });

        const order = {
            session_id: parseInt(sessionData.session_id),
            amount: Math.round(total),
            ticket: ticket,
            products: products,
            whatsapp_number: customerWhatsapp || null,
            company_data: {
                name: sessionData.company_data?.name || 'AUTOSERVICIO',
                vat: sessionData.company_data?.vat || '',
                street: sessionData.company_data?.street || 'N/A',
                city: sessionData.company_data?.city || 'N/A',
                turn: sessionData.company_data?.turn || 'COMERCIO',
                acteco: sessionData.company_data?.acteco || '471100'
            },
            tip_amount: 0,
            note: orderType === 'llevar' ? '[PARA LLEVAR]' : '[MESA / SERVIR]',
            payment_methods: paymentMethods?.length ? paymentMethods : (sessionData.payment_methods || []),
            simulate: forceSimulate === true
        };

        try {
            const activeProvider = await getPaymentProvider(apiUrl);
            setProvider(activeProvider);

            if (activeProvider === 'getnet' && !forceSimulate) {
                // Se exige el agente de pago (GoGetnet) Y la boleta electrónica (XSign) abiertos.
                const status = await getGetnetStatus(apiUrl);
                if (!status.agent) {
                    throw new Error('El sistema de pagos (Getnet) no está disponible en este momento. '
                        + 'Por favor acércate a caja.');
                }
                // XSign: chequeo ACTIVO con ping directo (no depende del contexto, que solo se
                // actualiza si el indicador está montado —y en el pago no lo está—).
                try {
                    await axios.get('http://127.0.0.1:5999/ping', { timeout: 3000 });
                } catch (e) {
                    throw new Error('La boleta electrónica (XSign) no está disponible en este momento. '
                        + 'Por favor acércate a caja.');
                }
            }

            const orderEndpoint = await getOrderEndpoint(apiUrl);
            console.log(`Enviando orden a API intermedia (${orderEndpoint})...`);

            const timeoutPromise = new Promise((_, reject) => {
                setTimeout(() => reject(new Error('TIMEOUT: La transaccion tardo mas de 160 segundos')), 160000);
            });

            const response = await Promise.race([
                axios.post(`${apiUrl}${orderEndpoint}`, order, {
                    timeout: 160000,
                    headers: { 'Content-Type': 'application/json' }
                }),
                timeoutPromise
            ]);

            if (!response.data.success) {
                throw new Error('Error en API intermedia: ' + (response.data.error || 'Error desconocido'));
            }

            console.log('Orden enviada exitosamente.');

            setIsFetching(false);
            markProcessCompleted();
            navigationRef.current = true;
            setCurrentStep('success');
            clearCart();
            setOrderType(null);
            navigate('/thank-you', { replace: true, state: { orderNumber: response.data.order_number } });

        } catch (err) {
            console.error('Error en proceso de pago:', err);

            let userErrorMessage = 'Error al procesar el pago. Intenta nuevamente.';

            const alreadyCharged = err.response?.data?.charged === true;
            const unconfirmed = err.response?.data?.unconfirmed === true;

            if (alreadyCharged || unconfirmed) {
                setChargedWithoutOrder(true);
                userErrorMessage = alreadyCharged
                    ? 'Tu pago fue realizado, pero no pudimos emitir la boleta. '
                      + 'NO vuelvas a pagar: acércate a caja con este mensaje.'
                    : (err.response?.data?.error
                        || 'No pudimos confirmar tu pago. NO vuelvas a pagar: acércate a caja con este mensaje.');
            } else if (err.message.includes('ECONNREFUSED')) {
                userErrorMessage = 'Error de conectividad con el sistema de pagos.';
            } else if (err.message.includes('timeout') || err.message.includes('TIMEOUT')) {
                userErrorMessage = 'Tiempo de espera agotado. Si tu tarjeta fue cobrada, acércate a caja antes de reintentar.';
            } else if (err.response?.data?.error) {
                userErrorMessage = err.response.data.error;
            } else {
                userErrorMessage = err.message || 'Error al procesar el pago.';
            }

            setError(userErrorMessage);
            setIsFetching(false);
            setIsTBKError(true);
            setTbkErrorMessage(userErrorMessage);
            setCurrentStep('error');
        } finally {
            setIsProcessing(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        hasCompleted, total, sessionData, items, apiUrl, clearCart, navigate,
        PAYMENT_SESSION_KEY, markProcessStarted, markProcessCompleted, provider, paymentMethods,
        isXSignConnected
    ]);

    const handleCancelPayment = useCallback(async () => {
        if (!isGetnet || isCancelling || hasCompleted) return;

        setIsCancelling(true);
        try {
            const result = await cancelGetnetSale(apiUrl);
            console.log('Cancelacion enviada al terminal:', result?.message || result);
        } catch (err) {
            console.error('No se pudo cancelar la venta en el terminal:', err.message);
        } finally {
            setIsCancelling(false);
        }
    }, [apiUrl, isGetnet, isCancelling, hasCompleted]);

    useEffect(() => {
        const alreadyStarted = sessionStorage.getItem(PAYMENT_SESSION_KEY + '_started') === 'true';
        const alreadyCompleted = sessionStorage.getItem(PAYMENT_SESSION_KEY + '_completed') === 'true';

        if (alreadyCompleted || alreadyStarted) {
            return;
        }

        if (currentStep === 'waiting' && !hasProcessStarted && !hasCompleted && !processRef.current && !isProcessing) {
            const timer = setTimeout(() => {
                const stillNotStarted = sessionStorage.getItem(PAYMENT_SESSION_KEY + '_started') !== 'true';
                if (!processRef.current && !hasCompleted && stillNotStarted) {
                    handlePayment();
                }
            }, 0);

            return () => clearTimeout(timer);
        }
    }, [currentStep, hasProcessStarted, hasCompleted, isProcessing, PAYMENT_SESSION_KEY, handlePayment]);

    const handleCancel = () => {
        if (!isProcessing && !hasCompleted) {
            navigate('/payment-method');
        }
    };

    const getStepContent = () => {
        switch (currentStep) {
            case 'waiting':
                return {
                    title: 'Preparando Terminal',
                    subtitle: 'Iniciando sistema de pagos...',
                    icon: 'preparing'
                };
            case 'processing':
                return {
                    title: 'Opere su Tarjeta',
                    subtitle: 'Inserte, deslice o acerque su tarjeta al terminal',
                    icon: 'card'
                };
            case 'success':
                return {
                    title: 'Pago Exitoso',
                    subtitle: 'Procesando venta y redirigiendo...',
                    icon: 'success'
                };
            case 'error':
                return {
                    title: 'Error en el Pago',
                    subtitle: 'Ha ocurrido un problema con la transaccion',
                    icon: 'error'
                };
            default:
                return {
                    title: 'Procesar Pago',
                    subtitle: 'Preparando...',
                    icon: 'preparing'
                };
        }
    };

    const content = getStepContent();

    return (
        <div className="operate-card-container" style={{ backgroundColor: 'transparent' }}>
            <BrandLogo delay={0.4} />

            <div style={{ position: 'relative', zIndex: 1, minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
                <div className="operate-header">
                    <div className="logo-section">
                        <img
                            src={logo}
                            alt="Danés"
                            className="header-logo"
                        />
                    </div>

                    {/* Volver solo cuando el pago falló: durante el proceso podría dejar un cobro a medias.
                        Si se cobró pero no se registró el pedido, tampoco (igual que el botón de reintentar). */}
                    {currentStep === 'error' && !chargedWithoutOrder && (
                        <button
                            onClick={handleCancel}
                            className="back-button"
                            disabled={isProcessing || hasCompleted}
                        >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M15 18l-6-6 6-6" />
                            </svg>
                        </button>
                    )}

                    <div className="header-content">
                        <h1 className="operate-title">{content.title}</h1>
                        <p className="operate-subtitle">{content.subtitle}</p>
                    </div>
                </div>

                <div className="operate-content">
                    <div className="content-container">
                        <div className="terminal-section">
                            <div className={`terminal-device ${currentStep}`}>
                                <div className="terminal-base">
                                    <div className="terminal-screen">
                                        <div className="screen-content">
                                            {currentStep === 'waiting' && (
                                                <div className="screen-text">Iniciando...</div>
                                            )}
                                            {currentStep === 'processing' && (
                                                <div className="screen-text">Procesar Pago</div>
                                            )}
                                            {currentStep === 'success' && (
                                                <div className="screen-text success">APROBADO</div>
                                            )}
                                            {currentStep === 'error' && (
                                                <div className="screen-text error">ERROR</div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="card-slot">
                                        <div className="slot-indicator"></div>
                                    </div>

                                    <div className="nfc-area">
                                        <div className={`nfc-waves ${currentStep === 'processing' ? 'active' : ''}`}>
                                            <div className="wave wave-1"></div>
                                            <div className="wave wave-2"></div>
                                            <div className="wave wave-3"></div>
                                        </div>
                                        <div className="nfc-icon">
                                            <svg viewBox="0 0 24 24" fill="currentColor">
                                                <path d="M6,15H8V9H6V15M12.5,12.5C12.5,13.3 11.8,14 11,14C10.2,14 9.5,13.3 9.5,12.5C9.5,11.7 10.2,11 11,11C11.8,11 12.5,11.7 12.5,12.5M16,19H8A3,3 0 0,1 5,16V8A3,3 0 0,1 8,5H16A3,3 0 0,1 19,8V16A3,3 0 0,1 16,19Z" />
                                            </svg>
                                        </div>
                                    </div>
                                </div>

                                {currentStep === 'processing' && (
                                    <div className={`payment-card step-${animationStep}`}>
                                        <div className="card-chip"></div>
                                        <div className="card-number">**** **** **** ****</div>
                                        <div className="card-logo">
                                            <svg viewBox="0 0 24 24" fill="currentColor">
                                                <path d="M20 4H4c-1.11 0-2 .89-2 2v12c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V6c0-1.11-.89-2-2-2zm0 14H4v-6h16v6zm0-10H4V6h16v2z" />
                                            </svg>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="status-section">
                            <div className="status-container">
                                <div className="amount-display">
                                    <div className="amount-label">Total a Pagar</div>
                                    <div className="amount-value">${total.toLocaleString()}</div>
                                </div>

                                <div className={`status-icon ${currentStep}`}>
                                    {currentStep === 'waiting' && (
                                        <div className="loading-spinner">
                                            <svg viewBox="0 0 24 24" fill="none">
                                                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" opacity="0.3" />
                                                <path d="M12 2 A10 10 0 0 1 22 12" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
                                            </svg>
                                        </div>
                                    )}

                                    {currentStep === 'processing' && (
                                        <div className="processing-icon">
                                            <svg viewBox="0 0 24 24" fill="currentColor">
                                                <path d="M20 4H4c-1.11 0-2 .89-2 2v12c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V6c0-1.11-.89-2-2-2zm0 14H4v-6h16v6zm0-10H4V6h16v2z" />
                                            </svg>
                                        </div>
                                    )}

                                    {currentStep === 'success' && (
                                        <div className="success-icon">
                                            <svg viewBox="0 0 24 24" fill="currentColor">
                                                <path d="M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22A10,10 0 0,1 2,12A10,10 0 0,1 12,2M11,16.5L18,9.5L16.59,8.09L11,13.67L7.91,10.59L6.5,12L11,16.5Z" />
                                            </svg>
                                        </div>
                                    )}

                                    {currentStep === 'error' && (
                                        <div className="error-icon">
                                            <svg viewBox="0 0 24 24" fill="currentColor">
                                                <path d="M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22A10,10 0 0,1 2,12A10,10 0 0,1 12,2M12,7A1.5,1.5 0 0,0 10.5,8.5A1.5,1.5 0 0,0 12,10A1.5,1.5 0 0,0 13.5,8.5A1.5,1.5 0 0,0 12,7M10.5,12A1.5,1.5 0 0,0 12,13.5A1.5,1.5 0 0,0 13.5,12A1.5,1.5 0 0,0 12,10.5A1.5,1.5 0 0,0 10.5,12Z" />
                                            </svg>
                                        </div>
                                    )}
                                </div>

                                <div className="status-message">
                                    {currentStep === 'waiting' && (
                                        <p>Sistema de pagos listo. Iniciando proceso...</p>
                                    )}
                                    {currentStep === 'processing' && (
                                        <div className="processing-steps">
                                            <p>Presente su tarjeta de una de estas formas:</p>
                                            <div className="payment-options">
                                                <div className="option">
                                                    <div className="option-icon">
                                                        <svg viewBox="0 0 24 24" fill="currentColor">
                                                            <path d="M2,6H22V4H2V6M2,18H22V16H2V18M2,12H22V10H2V12Z" />
                                                        </svg>
                                                    </div>
                                                    <span>Inserte</span>
                                                </div>
                                                <div className="option">
                                                    <div className="option-icon">
                                                        <svg viewBox="0 0 24 24" fill="currentColor">
                                                            <path d="M5,9C5,7.89 5.89,7 7,7H17C18.11,7 19,7.89 19,8V16C19,17.11 18.11,18 17,18H7C5.89,18 5,17.11 5,16V9M7,9V16H17V9H7Z" />
                                                        </svg>
                                                    </div>
                                                    <span>Deslice</span>
                                                </div>
                                                <div className="option">
                                                    <div className="option-icon">
                                                        <svg viewBox="0 0 24 24" fill="currentColor">
                                                            <path d="M6,15H8V9H6V15M12.5,12.5C12.5,13.3 11.8,14 11,14C10.2,14 9.5,13.3 9.5,12.5C9.5,11.7 10.2,11 11,11C11.8,11 12.5,11.7 12.5,12.5M16,19H8A3,3 0 0,1 5,16V8A3,3 0 0,1 8,5H16A3,3 0 0,1 19,8V16A3,3 0 0,1 16,19Z" />
                                                        </svg>
                                                    </div>
                                                    <span>Acerque</span>
                                                </div>
                                            </div>

                                            {isFetching && (
                                                <div className="tbk-status">
                                                    <p className="loading-text">Procesando transaccion {terminalName}...</p>
                                                </div>
                                            )}

                                            {isTBKError && tbkErrorMessage && (
                                                <div className="error-status">
                                                    <p className="error-text">Error {terminalName}: {tbkErrorMessage}</p>
                                                </div>
                                            )}

                                            {isGetnet && isFetching && (
                                                <button
                                                    className="cancel-payment-button"
                                                    onClick={handleCancelPayment}
                                                    disabled={isCancelling}
                                                >
                                                    {isCancelling ? 'Cancelando...' : 'Cancelar pago'}
                                                </button>
                                            )}
                                        </div>
                                    )}
                                    {currentStep === 'success' && (
                                        <div className="success-message">
                                            <p>Pago procesado exitosamente!</p>
                                            <p>Redirigiendo automaticamente...</p>
                                            <div className="success-checkmark">✓</div>
                                        </div>
                                    )}
                                    {currentStep === 'error' && (
                                        <div className="error-message">
                                            <p>{error}</p>
                                            {chargedWithoutOrder ? (
                                                <p className="charged-warning">
                                                    Muestra esta pantalla en caja para que registren tu pedido.
                                                </p>
                                            ) : (
                                                <button onClick={handleCancel} className="retry-button">
                                                    Volver e Intentar Nuevamente
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {!optimisticConnections.tbk || !optimisticConnections.xsign ? (
                                    <div className="connection-warnings">
                                        {!optimisticConnections.tbk && (
                                            <div className="warning-item">
                                                <small>Verificando conexion TBK...</small>
                                            </div>
                                        )}
                                        {!optimisticConnections.xsign && (
                                            <div className="warning-item">
                                                <small>Verificando conexion XSign...</small>
                                            </div>
                                        )}
                                    </div>
                                ) : null}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <style jsx>{`
                .operate-card-container {
                    min-height: 100vh;
                    display: flex;
                    flex-direction: column;
                    position: relative;
                }

                .operate-header {
                    background: #DA291C;
                    padding: 2rem;
                    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
                    position: relative;
                }

                .logo-section {
                    display: flex;
                    justify-content: center;
                    margin-bottom: 1.5rem;
                }

                .header-logo {
                    height: 120px;
                    width: auto;
                }

                .back-button {
                    position: absolute;
                    left: 2rem;
                    top: 50%;
                    transform: translateY(-50%);
                    width: 56px;
                    height: 56px;
                    background: rgba(255, 255, 255, 0.2);
                    border: none;
                    border-radius: 50%;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 200ms ease;
                }

                .back-button:hover:not(:disabled) {
                    background: rgba(255, 255, 255, 0.3);
                    transform: translateY(-50%) scale(1.1);
                }

                .back-button:disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                }

                .back-button svg {
                    width: 28px;
                    height: 28px;
                    color: white;
                }

                .header-content {
                    text-align: center;
                    color: white;
                }

                .operate-title {
                    font-size: 3rem;
                    font-weight: 900;
                    margin: 0 0 0.5rem 0;
                    color: white;
                }

                .operate-subtitle {
                    font-size: 1.25rem;
                    margin: 0;
                    color: rgba(255, 255, 255, 0.9);
                    font-weight: 600;
                }

                .operate-content {
                    flex: 1;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 4rem 2rem;
                }

                .content-container {
                    max-width: 1200px;
                    width: 100%;
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 4rem;
                    align-items: center;
                }

                .terminal-section {
                    display: flex;
                    justify-content: center;
                    align-items: center;
                }

                .terminal-device {
                    position: relative;
                    perspective: 1000px;
                    animation: deviceEnter 800ms ease-out;
                }

                .terminal-base {
                    width: 300px;
                    height: 400px;
                    background: linear-gradient(145deg, #2d3748, #1a202c);
                    border-radius: 24px;
                    padding: 2rem;
                    box-shadow: 
                        0 20px 40px rgba(0, 0, 0, 0.4),
                        inset 0 2px 0 rgba(255, 255, 255, 0.1);
                    display: flex;
                    flex-direction: column;
                    gap: 2rem;
                    position: relative;
                    overflow: hidden;
                }

                .terminal-screen {
                    width: 100%;
                    height: 120px;
                    background: linear-gradient(145deg, #1a202c, #0d1117);
                    border-radius: 12px;
                    border: 2px solid #4a5568;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    box-shadow: inset 0 4px 8px rgba(0, 0, 0, 0.6);
                }

                .screen-content {
                    text-align: center;
                }

                .screen-text {
                    color: #00ff88;
                    font-family: 'Courier New', monospace;
                    font-size: 1.125rem;
                    font-weight: bold;
                    text-shadow: 0 0 8px currentColor;
                    animation: screenFlicker 2s ease-in-out infinite;
                }

                .screen-text.success {
                    color: #00ff00;
                    animation: successPulse 1s ease-in-out infinite;
                }

                .screen-text.error {
                    color: #ff4444;
                    animation: errorFlash 0.5s ease-in-out infinite;
                }

                .card-slot {
                    width: 100%;
                    height: 8px;
                    background: linear-gradient(145deg, #0d1117, #1a202c);
                    border-radius: 4px;
                    box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.8);
                    position: relative;
                }

                .slot-indicator {
                    position: absolute;
                    top: 50%;
                    left: 8px;
                    transform: translateY(-50%);
                    width: 4px;
                    height: 4px;
                    background: #4ade80;
                    border-radius: 50%;
                    box-shadow: 0 0 8px currentColor;
                    animation: slotBlink 2s ease-in-out infinite;
                }

                .nfc-area {
                    position: relative;
                    width: 80px;
                    height: 80px;
                    background: radial-gradient(circle, #4f46e520, transparent);
                    border-radius: 50%;
                    margin: 0 auto;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border: 2px dashed #4f46e5;
                }

                .nfc-waves {
                    position: absolute;
                    inset: 0;
                    opacity: 0;
                    transition: opacity 300ms ease;
                }

                .nfc-waves.active {
                    opacity: 1;
                }

                .wave {
                    position: absolute;
                    border: 2px solid #4f46e5;
                    border-radius: 50%;
                    animation: nfcWave 2s ease-out infinite;
                }

                .wave-1 {
                    inset: 10%;
                    animation-delay: 0s;
                }

                .wave-2 {
                    inset: 5%;
                    animation-delay: 0.3s;
                }

                .wave-3 {
                    inset: 0%;
                    animation-delay: 0.6s;
                }

                .nfc-icon {
                    width: 32px;
                    height: 32px;
                    color: #4f46e5;
                    z-index: 2;
                    position: relative;
                }

                .payment-card {
                    position: absolute;
                    top: 50%;
                    left: 50%;
                    width: 200px;
                    height: 125px;
                    background: linear-gradient(135deg, #1e40af, #3b82f6);
                    border-radius: 12px;
                    padding: 1rem;
                    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3);
                    color: white;
                    display: flex;
                    flex-direction: column;
                    justify-content: space-between;
                    transform-origin: center;
                    transition: all 800ms cubic-bezier(0.4, 0, 0.2, 1);
                }

                .payment-card.step-0 {
                    transform: translate(-50%, -50%) translateY(-100px) rotateX(0deg);
                }

                .payment-card.step-1 {
                    transform: translate(-50%, -50%) translateY(0px) rotateX(-10deg);
                }

                .payment-card.step-2 {
                    transform: translate(-50%, -50%) translateY(100px) rotateX(0deg);
                }

                .card-chip {
                    width: 32px;
                    height: 24px;
                    background: linear-gradient(145deg, #fbbf24, #f59e0b);
                    border-radius: 4px;
                    align-self: flex-start;
                }

                .card-number {
                    font-family: 'Courier New', monospace;
                    font-size: 1rem;
                    font-weight: bold;
                    text-align: center;
                    letter-spacing: 2px;
                }

                .card-logo {
                    width: 32px;
                    height: 32px;
                    align-self: flex-end;
                    opacity: 0.8;
                }

                .status-section {
                    display: flex;
                    justify-content: center;
                    align-items: center;
                }

                .status-container {
                    background: #E5DFD2;
                    border: 3px solid #DA291C;
                    border-radius: 24px;
                    padding: 3rem;
                    box-shadow: 0 6px 25px rgba(0, 0, 0, 0.15);
                    text-align: center;
                    max-width: 400px;
                    width: 100%;
                    animation: statusEnter 800ms ease-out 200ms both;
                }

                .amount-display {
                    margin-bottom: 2rem;
                    padding: 1.5rem;
                    background: white;
                    border-radius: 16px;
                    border: 2px solid #DA291C;
                }

                .amount-label {
                    font-size: 1rem;
                    color: #6b7280;
                    margin-bottom: 0.5rem;
                    font-weight: 600;
                }

                .amount-value {
                    font-size: 3.5rem;
                    font-weight: 900;
                    color: #DA291C;
                    line-height: 1;
                }

                .status-icon {
                    width: 120px;
                    height: 120px;
                    margin: 0 auto 2rem;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 300ms ease;
                }

                .status-icon.waiting {
                    background: linear-gradient(135deg, #10b981, #059669);
                    animation: waitingPulse 2s ease-in-out infinite;
                }

                .status-icon.processing {
                    background: linear-gradient(135deg, #DA291C, #DA291Cdd);
                    animation: processingPulse 2s ease-in-out infinite;
                }

                .status-icon.success {
                    background: linear-gradient(135deg, #10b981, #059669);
                    animation: successBounce 0.8s ease-out;
                }

                .status-icon.error {
                    background: linear-gradient(135deg, #ef4444, #dc2626);
                    animation: errorShake 0.5s ease-out;
                }

                .loading-spinner svg {
                    width: 60px;
                    height: 60px;
                    color: white;
                    animation: spin 1s linear infinite;
                }

                .processing-icon svg,
                .success-icon svg,
                .error-icon svg {
                    width: 60px;
                    height: 60px;
                    color: white;
                }

                .status-message {
                    color: #374151;
                }

                .status-message p {
                    font-size: 1.125rem;
                    margin: 0 0 1rem 0;
                    font-weight: 600;
                }

                .processing-steps p {
                    margin-bottom: 2rem;
                }

                .payment-options {
                    display: flex;
                    justify-content: space-around;
                    gap: 1rem;
                    margin-bottom: 1.5rem;
                }

                .option {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 0.5rem;
                    padding: 1rem;
                    background: white;
                    border-radius: 12px;
                    flex: 1;
                }

                .option-icon {
                    width: 40px;
                    height: 40px;
                    color: #DA291C;
                }

                .option span {
                    font-size: 0.875rem;
                    font-weight: 700;
                    color: #374151;
                }

                .tbk-status {
                    margin-top: 1rem;
                    padding: 2rem;
                    background: linear-gradient(135deg, #DA291C15, #DA291C10);
                    border-radius: 8px;
                    border: 1px solid #DA291C30;
                }

                .loading-text {
                    color: #DA291C;
                    font-size: 0.875rem;
                    font-weight: 600;
                    margin: 0;
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                }

                .error-status {
                    margin-top: 1rem;
                    padding: 0.75rem;
                    background: rgba(239, 68, 68, 0.1);
                    border-radius: 8px;
                    border: 1px solid rgba(239, 68, 68, 0.3);
                }

                .error-text {
                    color: #ef4444;
                    font-size: 0.875rem;
                    font-weight: 600;
                    margin: 0;
                }

                .success-message {
                    color: #10b981;
                }

                .success-checkmark {
                    font-size: 3rem;
                    font-weight: bold;
                    margin-top: 1rem;
                    animation: checkmarkPulse 2s ease-in-out infinite;
                }

                .error-message {
                    color: #ef4444;
                }

                .charged-warning {
                    margin-top: 1rem;
                    padding: 1rem;
                    background: rgba(245, 158, 11, 0.12);
                    border: 2px solid #d97706;
                    border-radius: 12px;
                    color: #92400e;
                    font-weight: 700;
                }

                .retry-button {
                    margin-top: 1rem;
                    padding: 1rem 2rem;
                    background: #DA291C;
                    color: white;
                    border: none;
                    border-radius: 12px;
                    font-weight: 700;
                    cursor: pointer;
                    transition: all 200ms ease;
                }

                .retry-button:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 8px 25px #DA291C50;
                }

                .cancel-payment-button {
                    margin-top: 1rem;
                    width: 100%;
                    padding: 1rem 1.5rem;
                    background: transparent;
                    color: #b91c1c;
                    border: 2px solid #b91c1c;
                    border-radius: 12px;
                    font-family: 'Quicksand', sans-serif;
                    font-size: 1.05rem;
                    font-weight: 700;
                    cursor: pointer;
                    transition: all 200ms ease;
                }

                .cancel-payment-button:hover:not(:disabled) {
                    background: #b91c1c;
                    color: #ffffff;
                }

                .cancel-payment-button:disabled {
                    opacity: 0.4;
                    cursor: not-allowed;
                }

                .simulate-approve-button {
                    margin-top: 1.5rem;
                    width: 100%;
                    padding: 1rem 1.5rem;
                    background: transparent;
                    color: #6b7280;
                    border: 2px dashed #6b7280;
                    border-radius: 12px;
                    font-family: 'Quicksand', sans-serif;
                    font-size: 1.05rem;
                    font-weight: 700;
                    cursor: pointer;
                    transition: all 200ms ease;
                }

                .simulate-approve-button:hover:not(:disabled) {
                    background: #6b7280;
                    color: #ffffff;
                }

                .simulate-approve-button:disabled {
                    opacity: 0.4;
                    cursor: not-allowed;
                }

                .connection-warnings {
                    margin-top: 1rem;
                    padding: 0.75rem;
                    background: rgba(245, 158, 11, 0.1);
                    border-radius: 8px;
                    border: 1px solid rgba(245, 158, 11, 0.3);
                }

                .warning-item {
                    margin: 0.25rem 0;
                }

                .warning-item small {
                    color: #d97706;
                    font-size: 0.75rem;
                }

                @keyframes deviceEnter {
                    0% {
                        opacity: 0;
                        transform: translateY(40px) rotateY(15deg);
                    }
                    100% {
                        opacity: 1;
                        transform: translateY(0) rotateY(0deg);
                    }
                }

                @keyframes statusEnter {
                    0% {
                        opacity: 0;
                        transform: translateX(40px);
                    }
                    100% {
                        opacity: 1;
                        transform: translateX(0);
                    }
                }

                @keyframes screenFlicker {
                    0%, 100% { opacity: 1; }
                    50% { opacity: 0.8; }
                }

                @keyframes successPulse {
                    0%, 100% { opacity: 1; transform: scale(1); }
                    50% { opacity: 0.8; transform: scale(1.05); }
                }

                @keyframes errorFlash {
                    0%, 100% { opacity: 1; }
                    50% { opacity: 0.3; }
                }

                @keyframes slotBlink {
                    0%, 100% { opacity: 1; }
                    50% { opacity: 0.3; }
                }

                @keyframes nfcWave {
                    0% {
                        opacity: 1;
                        transform: scale(0.5);
                    }
                    100% {
                        opacity: 0;
                        transform: scale(1.2);
                    }
                }

                @keyframes waitingPulse {
                    0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.4); }
                    50% { transform: scale(1.05); box-shadow: 0 0 0 10px rgba(16, 185, 129, 0); }
                }

                @keyframes processingPulse {
                    0%, 100% { transform: scale(1); }
                    50% { transform: scale(1.05); }
                }

                @keyframes successBounce {
                    0% { transform: scale(0); }
                    50% { transform: scale(1.2); }
                    100% { transform: scale(1); }
                }

                @keyframes errorShake {
                    0%, 100% { transform: translateX(0); }
                    25% { transform: translateX(-5px); }
                    75% { transform: translateX(5px); }
                }

                @keyframes checkmarkPulse {
                    0%, 100% { transform: scale(1); opacity: 1; }
                    50% { transform: scale(1.1); opacity: 0.8; }
                }

                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }

                @media (max-width: 768px) {
                    .content-container {
                        grid-template-columns: 1fr;
                        gap: 2rem;
                    }
                    
                    .operate-title {
                        font-size: 2rem;
                    }
                    
                    .payment-options {
                        flex-direction: column;
                    }
                    
                    .terminal-base {
                        width: 250px;
                        height: 350px;
                    }

                    .back-button {
                        top: 2rem;
                        transform: translateY(0);
                    }

                    .back-button:hover:not(:disabled) {
                        transform: scale(1.1);
                    }
                }
            `}</style>
        </div>
    );
};

export default OperateCardScreen;